// NOTE: cart events are NOT bus events. They live on the frozen DOM vocabulary
// in assets/fw-events.js (`Theme.events.NAMES.cartUpdate/cartAdded/cartError`),
// dispatched only by Alpine.store('cart') — the single cart writer (issue #18).
export const Events = {
  header:  { state: 'store:header:state' },
  menu:    { open: 'store:menu:open', close: 'store:menu:close' },
  drawer:  { open: 'store:menuDrawer:open', close: 'store:menuDrawer:close', subOpen: 'store:menuSubdrawer:open', subClose: 'store:menuSubdrawer:close' },
  variant: { selected: 'store:variant:selected', update: 'store:variant:update', media: 'store:variant:media' },
  page:    { visible: 'store:page:visible', hidden: 'store:page:hidden', scrollDepth: 'store:page:scrollDepth', click: 'store:page:click' },
  card:    {
    click: 'store:card:click',
    select: 'store:card:select',
    hoverStart: 'store:card:hover:start',
    hoverEnd: 'store:card:hover:end',
    focus: 'store:card:focus',
    blur: 'store:card:blur',
    hoverChildStart: 'store:card:hover:child:start',
    hoverChildEnd: 'store:card:hover:child:end'
  }
};

// Backwards-compatible logger on window.store
try {
  const root = window;
  const store = (root.store = root.store || {});
  if (typeof store.debug === 'undefined') store.debug = false;
  store.setDebug = (v) => { try { store.debug = !!v; } catch (_) {} };
  store.log = (...args) => { try { if (store.debug) console.log('[store]', ...args); } catch (_) {} };
  store.warn = (...args) => { try { console.warn('[store]', ...args); } catch (_) {} };
  store.error = (...args) => { try { console.error('[store]', ...args); } catch (_) {} };
} catch (_) {}

const defaultTarget = () => document;

export const emit = (name, detail, target = defaultTarget()) => {
  try { target && target.dispatchEvent(new CustomEvent(name, { detail, bubbles: true })); } catch (_) {}
  // Mirror to analytics plugins (best-effort)
  try { PLUGINS.forEach((fn) => { try { fn({ type: 'event', name, detail }); } catch (_) {} }); } catch (_) {}
  try { if (window.store && window.store.debug) window.store.log('[events.emit]', name, detail); } catch (_) {}
};

export const on = (name, handler, opts) => {
  const tgt = (opts && opts.target) || defaultTarget();
  const options = (opts && opts.options) || false;
  const safe = (ev) => { try { handler(ev); } catch (e) { try { window.store && window.store.warn && window.store.warn('[events.on] handler error', e, { name }); } catch (_) {} } };
  try { tgt.addEventListener(name, safe, options); } catch (_) {}
  return () => { try { tgt.removeEventListener(name, safe, options); } catch (_) {} };
};

export const once = (name, handler, opts) => {
  const un = on(name, (ev) => { try { handler(ev); } finally { try { un(); } catch (_) {} } }, opts);
  return un;
};

export const off = (name, handler, opts) => {
  const tgt = (opts && opts.target) || defaultTarget();
  const options = (opts && opts.options) || false;
  try { tgt.removeEventListener(name, handler, options); } catch (_) {}
};

// Analytics: simple plugin system + track()
const PLUGINS = [];

export const use = (plugin) => {
  if (!plugin) return () => {};
  const fn = (typeof plugin === 'function') ? plugin : (plugin && plugin.handle);
  if (typeof fn !== 'function') return () => {};
  PLUGINS.push(fn);
  return () => { const i = PLUGINS.indexOf(fn); if (i >= 0) PLUGINS.splice(i, 1); };
};

export const track = (name, props) => {
  try {
    PLUGINS.forEach((fn) => { try { fn({ type: 'track', name, props }); } catch (_) {} });
    if (window.store && window.store.debug) window.store.log('[track]', name, props);
  } catch (_) {}
};

// Default PostHog plugin (no-op if absent)
use(({ type, name, props }) => {
  try { if (type !== 'track') return; if (typeof window.posthog === 'undefined') return; window.posthog.capture(name, props); } catch (_) {}
});

// Attribute-based tracking: preferred [data-track], back-compat [data-ph-event]
try {
  document.addEventListener('click', (ev) => {
    try {
      const el = ev.target && ev.target.closest && ev.target.closest('[data-track],[data-ph-event]');
      if (!el) return;
      const name = el.getAttribute('data-track') || el.getAttribute('data-ph-event');
      if (!name) return;
      let props = undefined;
      const raw = el.getAttribute('data-track-props') || el.getAttribute('data-ph-props') || '';
      if (raw && raw.trim() !== '') { try { props = JSON.parse(raw); } catch (e) { try { window.store && window.store.warn && window.store.warn('[track] JSON parse error', e, raw); } catch (_) {} } }
      track(name, props);
    } catch (e) { try { window.store && window.store.warn && window.store.warn('[track] delegation error', e); } catch (_) {} }
  }, true);
} catch (_) {}

// Global bridge
try { if (typeof window !== 'undefined') { window.storeEvents = { Events, emit, on, off, once, track, use }; } } catch (_) {}

